"use client";
/**
 * Hooks for the Block 5 loyalty-expansion surfaces: tier benefits,
 * challenges + streaks, badges, referrals.
 *
 * All of them are tolerant of a backend that isn't deployed yet:
 *  - 404 → treated as "feature not available" (enabled: false, empty data)
 *  - network / 5xx → error state the screens surface with a retry
 *  - signed-out → idle state, no fetch
 */
import { useCallback, useEffect, useMemo, useState } from "react";

import {
  benefitsApi,
  gamificationApi,
  referralApi,
  type BenefitsResolveResponse,
  type CustomerBadge,
  type CustomerChallenge,
  type CustomerChallengesResponse,
  type CustomerStreaks,
  type ReferralResponse,
  type ResolvedBenefit,
  type TierGrace,
  type TierLadderEntry,
  type TierLadderResponse,
} from "@/api/services";
import { ApiError } from "@/api/http";
import { useAuth } from "@/store/auth";

type AsyncState<T> = {
  loading: boolean;
  error: Error | null;
  data: T | null;
  /** True when the endpoint 404'd (backend not deployed / feature off). */
  unavailable: boolean;
  refresh: () => Promise<void>;
};

function useCustomerResource<T>(
  fetcher: (customerId: string) => Promise<T>,
): AsyncState<T> {
  const { isLoggedIn, user } = useAuth();
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [unavailable, setUnavailable] = useState(false);

  const refresh = useCallback(async () => {
    if (!isLoggedIn || !user?.id) {
      setData(null);
      setError(null);
      setLoading(false);
      setUnavailable(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await fetcher(user.id);
      setData(res);
      setUnavailable(false);
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        // Endpoint not deployed yet, or the customer has no loyalty account.
        setData(null);
        setError(null);
        setUnavailable(true);
      } else {
        console.warn("[loyalty-expansion] fetch failed", err);
        setError(err instanceof Error ? err : new Error(String(err)));
        setData(null);
        setUnavailable(false);
      }
    } finally {
      setLoading(false);
    }
  }, [isLoggedIn, user?.id, fetcher]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return { loading, error, data, unavailable, refresh };
}

/* ──────── Benefits ──────── */

export type UseBenefitsResult = {
  loading: boolean;
  error: Error | null;
  /** null until loaded; tierId null inside means "no loyalty account". */
  resolved: BenefitsResolveResponse | null;
  benefits: ResolvedBenefit[];
  tierName: string | null;
  tierGrace: TierGrace | null;
  unavailable: boolean;
  refresh: () => Promise<void>;
};

function asPlainString(v: unknown, lang: string): string | null {
  if (typeof v === "string" && v) return v;
  if (v && typeof v === "object") {
    const o = v as Record<string, unknown>;
    const norm = lang.toLowerCase().split(/[-_]/)[0];
    for (const k of [lang, norm, "en", "ar"]) {
      if (typeof o[k] === "string" && o[k]) return o[k] as string;
    }
    for (const val of Object.values(o)) {
      if (typeof val === "string" && val) return val;
    }
  }
  return null;
}

/** Pick a display string out of a string-or-LocalizedString field. */
export function localizedField(v: unknown, lang: string): string | null {
  return asPlainString(v, lang);
}

export function useBenefits(lang: string): UseBenefitsResult {
  const fetcher = useCallback(
    (customerId: string) => benefitsApi.resolve(customerId, {}),
    [],
  );
  const { loading, error, data, unavailable, refresh } =
    useCustomerResource<BenefitsResolveResponse>(fetcher);

  const benefits = useMemo(
    () => (Array.isArray(data?.benefits) ? data.benefits : []),
    [data],
  );
  const tierName = useMemo(
    () => asPlainString(data?.tierName, lang),
    [data, lang],
  );
  const tierGrace = data?.tierGrace ?? null;

  return { loading, error, resolved: data, benefits, tierName, tierGrace, unavailable, refresh };
}

/* ──────── Tier ladder (tenant-defined; not customer-specific) ──────── */

export type UseTierLadderResult = {
  loading: boolean;
  /** The tenant's real tiers, ordered low→high. Empty when no active program. */
  tiers: TierLadderEntry[];
};

/**
 * Loads the tenant's tier ladder once on mount. Tenant-wide (no customerId), so
 * it works for not-yet-enrolled and signed-out customers. Fail-soft: any error
 * (incl. 404 before deploy) yields an empty ladder, so the no-tier explainer
 * falls back to neutral copy instead of inventing tier names.
 */
export function useTierLadder(): UseTierLadderResult {
  const [loading, setLoading] = useState(true);
  const [tiers, setTiers] = useState<TierLadderEntry[]>([]);

  useEffect(() => {
    let alive = true;
    benefitsApi
      .listTiers()
      .then((res: TierLadderResponse) => {
        if (alive) setTiers(Array.isArray(res?.tiers) ? res.tiers : []);
      })
      .catch(() => {
        if (alive) setTiers([]);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, []);

  return { loading, tiers };
}

/* ──────── Challenges + streaks ──────── */

export type UseChallengesResult = {
  loading: boolean;
  error: Error | null;
  challengesEnabled: boolean;
  streaksEnabled: boolean;
  active: CustomerChallenge[];
  completed: CustomerChallenge[];
  streaks: CustomerStreaks | null;
  unavailable: boolean;
  refresh: () => Promise<void>;
};

function challengeKey(c: CustomerChallenge, idx: number): string {
  return String(c.id ?? c.challengeId ?? `challenge-${idx}`);
}

function isCompleted(c: CustomerChallenge): boolean {
  if (c.completedAt) return true;
  const status = (c.status ?? "").toLowerCase();
  if (status === "completed" || status === "done") return true;
  const target = c.target ?? c.criteria?.target ?? null;
  if (
    typeof c.progress === "number" &&
    typeof target === "number" &&
    target > 0
  ) {
    return c.progress >= target;
  }
  return false;
}

export function useChallenges(): UseChallengesResult {
  const fetcher = useCallback(
    (customerId: string) => gamificationApi.getChallenges(customerId),
    [],
  );
  const { loading, error, data, unavailable, refresh } =
    useCustomerResource<CustomerChallengesResponse>(fetcher);

  const challengesEnabled =
    !unavailable && (data?.challengesEnabled ?? data?.enabled ?? false);
  const streaksEnabled =
    !unavailable && (data?.streaksEnabled ?? data?.streaks?.enabled ?? false);

  const { active, completed } = useMemo(() => {
    const all: CustomerChallenge[] = [];
    const seen = new Set<string>();
    const push = (list?: CustomerChallenge[] | null) => {
      if (!Array.isArray(list)) return;
      for (const [i, c] of list.entries()) {
        if (!c) continue;
        const k = challengeKey(c, all.length + i);
        if (seen.has(k)) continue;
        seen.add(k);
        all.push(c);
      }
    };
    push(data?.active);
    push(data?.challenges);
    push(data?.completed);
    push(data?.history);
    return {
      active: all.filter((c) => !isCompleted(c)),
      completed: all.filter(isCompleted),
    };
  }, [data]);

  return {
    loading,
    error,
    challengesEnabled,
    streaksEnabled,
    active,
    completed,
    streaks: data?.streaks ?? null,
    unavailable,
    refresh,
  };
}

/* ──────── Streak presentation ──────── */

export type StreakWidgetState = "active" | "at_risk" | "milestone" | "broken";

export type StreakView = {
  type: "weeklyVisit" | "monthlyOrder";
  count: number;
  state: StreakWidgetState;
  /** Count needed for the next milestone reward, when configured. */
  nextMilestoneCount: number | null;
};

function isoWeekKey(d: Date): string {
  // ISO-8601 week number, Thursday trick.
  const date = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const dayNum = date.getUTCDay() || 7;
  date.setUTCDate(date.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
  const weekNo = Math.ceil(((date.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
  return `${date.getUTCFullYear()}-W${String(weekNo).padStart(2, "0")}`;
}

function monthKey(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function periodKeys(type: "weeklyVisit" | "monthlyOrder"): {
  current: string;
  previous: string;
} {
  const now = new Date();
  if (type === "weeklyVisit") {
    const prev = new Date(now.getTime() - 7 * 86400000);
    return { current: isoWeekKey(now), previous: isoWeekKey(prev) };
  }
  const prev = new Date(now.getFullYear(), now.getMonth() - 1, 15);
  return { current: monthKey(now), previous: monthKey(prev) };
}

/**
 * Derive the design's four widget states (active / at-risk / milestone /
 * broken) from the backend's `{count, lastPeriodKey}` representation.
 */
export function deriveStreakView(
  streaks: CustomerStreaks | null,
): StreakView | null {
  if (!streaks) return null;
  const candidates: Array<["weeklyVisit" | "monthlyOrder", typeof streaks.weeklyVisit]> = [
    ["weeklyVisit", streaks.weeklyVisit],
    ["monthlyOrder", streaks.monthlyOrder],
  ];
  // Prefer the streak with the highest count; fall back to weeklyVisit.
  let best: StreakView | null = null;
  for (const [type, info] of candidates) {
    if (!info) continue;
    const count = typeof info.count === "number" ? info.count : 0;
    const last = typeof info.lastPeriodKey === "string" ? info.lastPeriodKey : null;
    const { current, previous } = periodKeys(type);

    let state: StreakWidgetState;
    if (count <= 0 || !last) {
      state = "broken";
    } else if (last === current) {
      state = "active";
    } else if (last === previous) {
      state = "at_risk";
    } else {
      state = "broken";
    }

    const milestones = Array.isArray(streaks.milestones) ? streaks.milestones : [];
    const typeKey = type === "weeklyVisit" ? "weekly_visit" : "monthly_order";
    const relevant = milestones
      .filter((m) => !m.type || m.type === typeKey || m.type === type)
      .map((m) => m.count ?? 0)
      .filter((c) => c > 0)
      .sort((a, b) => a - b);
    const hitMilestone = relevant.includes(count) && state === "active";
    const nextMilestoneCount = relevant.find((c) => c > count) ?? null;
    if (hitMilestone) state = "milestone";

    const view: StreakView = { type, count, state, nextMilestoneCount };
    if (!best || count > best.count) best = view;
  }
  return best;
}

/* ──────── Badges ──────── */

export type BadgeBucket = "earned" | "in_progress" | "locked";

export type BadgeView = {
  key: string;
  badge: CustomerBadge;
  bucket: BadgeBucket;
  /** 0..1 */
  progress: number | null;
};

export type UseBadgesResult = {
  loading: boolean;
  error: Error | null;
  badgesEnabled: boolean;
  badges: BadgeView[];
  unavailable: boolean;
  refresh: () => Promise<void>;
};

function normalizeProgress(b: CustomerBadge): number | null {
  const raw =
    typeof b.progressPct === "number"
      ? b.progressPct / 100
      : typeof b.progress === "number"
        ? b.progress > 1
          ? b.progress / 100
          : b.progress
        : null;
  if (raw === null || !Number.isFinite(raw)) return null;
  return Math.max(0, Math.min(1, raw));
}

function bucketOf(b: CustomerBadge, hinted?: BadgeBucket): BadgeBucket {
  if (b.earned || b.awardedAt) return "earned";
  const status = (b.status ?? "").toLowerCase();
  if (status === "earned" || status === "awarded") return "earned";
  if (b.locked || b.hidden || status === "locked" || status === "hidden") return "locked";
  const p = normalizeProgress(b);
  if (p !== null && p > 0) return "in_progress";
  return hinted ?? "locked";
}

export function useBadges(): UseBadgesResult {
  const fetcher = useCallback(
    (customerId: string) => gamificationApi.getBadges(customerId),
    [],
  );
  const { loading, error, data, unavailable, refresh } = useCustomerResource(fetcher);

  const badgesEnabled =
    !unavailable && (data?.badgesEnabled ?? data?.enabled ?? false);

  const badges = useMemo<BadgeView[]>(() => {
    const out: BadgeView[] = [];
    const seen = new Set<string>();
    const push = (list?: CustomerBadge[] | null, hinted?: BadgeBucket) => {
      if (!Array.isArray(list)) return;
      for (const b of list) {
        if (!b) continue;
        const key = String(b.badgeId ?? b.id ?? `badge-${out.length}`);
        if (seen.has(key)) continue;
        seen.add(key);
        out.push({ key, badge: b, bucket: bucketOf(b, hinted), progress: normalizeProgress(b) });
      }
    };
    push(data?.earned, "earned");
    push(data?.inProgress, "in_progress");
    push(data?.badges);
    push(data?.locked, "locked");
    return out;
  }, [data]);

  return { loading, error, badgesEnabled, badges, unavailable, refresh };
}

/* ──────── Referrals ──────── */

export type UseReferralResult = {
  loading: boolean;
  error: Error | null;
  referral: ReferralResponse | null;
  /** False when the tenant disabled referrals (or the endpoint 404s). */
  enabled: boolean;
  unavailable: boolean;
  refresh: () => Promise<void>;
};

export function useReferral(): UseReferralResult {
  const fetcher = useCallback(
    (customerId: string) => referralApi.get(customerId),
    [],
  );
  const { loading, error, data, unavailable, refresh } =
    useCustomerResource<ReferralResponse>(fetcher);

  const enabled = !unavailable && !!data?.code && data?.enabled !== false;

  return { loading, error, referral: data, enabled, unavailable, refresh };
}
